import * as React from 'react';

import { Box, useScrollTrigger } from '@mui/material';

import { CollabInvite } from '../../components/Flows/CollabInvite/CollabInvite';
import InfiniteScroll from '../../components/InfiniteScroll/InfiniteScroll';
import { CollabUsersTable } from '../../components/Tables/CollabUsers/CollabUsers';
import { UpgradePlanOverlay } from '../../components/UpgradePlanOverlay/UpgradePlanOverlay';
import { User, VenueListUsersFilter } from '../../graphql/API';
import useListUserQuery from '../../hooks/reactQuery/useListUserQuery';
import useVenueId from '../../hooks/useVenueId';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import Header from './Header';
import ProfileCheckBoxGroup from './components/ProfileCheckBoxGroup';
import useCollabsReducer, { collabsDefaultFilters } from './reducer';

export type PageView = 'list' | 'grid';

const Collabs: React.FC = () => {
  const venueId = useVenueId();
  const [state, dispatch] = useCollabsReducer();
  const { selected, filters } = state;

  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const [isCollabFlowActive, setIsCollabFlowActive] = React.useState(false);
  const [pageView, setPageView] = React.useState<PageView>('grid');

  const elementRef = React.useRef<HTMLDivElement>();
  const inScroll = useScrollTrigger({
    threshold: 10,
    disableHysteresis: true,
    target: elementRef.current,
  });

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const { data, isFetching, isLoading, fetchNextPage, hasNextPage } = useListUserQuery(
    venueId,
    filters,
    venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan ? 15 : 50,
  );

  const getSelectedUsersData = React.useCallback(
    (ssUsers: string[]) =>
      ssUsers.map((x) => data.find((y) => x === y.id)).filter(Boolean) as User[],
    [data],
  );

  React.useEffect(() => {
    if (!venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan) {
        dispatch({ type: 'SET_FILTERS', payload: { market: false } });
      }
    } else if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      dispatch({
        type: 'SET_FILTERS',
        payload: { market: false },
      });
    } else {
      dispatch({
        type: 'SET_FILTERS',
        payload: { marketRegion: [] },
      });
    }
  }, [venueState, subscriptionPlan, dispatch]);

  const handleUserSelection = (selectedUsers: string[]) => {
    const previousUsers = (state.selected.users || []).filter((x) => selectedUsers.includes(x.id));
    const _users = Array.from(
      new Map(
        [...previousUsers, ...getSelectedUsersData(selectedUsers)].map((user) => [user.id, user]),
      ).values(),
    );

    dispatch({
      type: 'SET_SELECTED_USERS',
      payload: {
        values: _users.map((user) => user.id),
        users: _users,
      },
    });
  };

  const handleCollabInvite = () => {
    setIsCollabFlowActive((prev) => !prev);
  };

  const onSuccess = () => {
    setIsCollabFlowActive(false);
    dispatch({ type: 'RESET_SELECTED_USERS' });
  };

  const handleMarketChange = () => {
    if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      dispatch({ type: 'SET_FILTERS', payload: { marketRegion: [], market: false } });
    } else {
      dispatch({ type: 'SET_FILTERS', payload: { market: false } });
    }
  };

  const handleSorting = (filters: Partial<VenueListUsersFilter> | undefined) => {
    dispatch({ type: 'SET_FILTERS', payload: { ...filters } });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        backgroundColor: 'white',
        position: 'relative',
      }}
    >
      <Header
        reducer={[state, dispatch]}
        inScroll={inScroll}
        handleInvite={handleCollabInvite}
        isLoading={isFetching || isLoading}
        isCollabFlowActive={isCollabFlowActive}
        pageView={pageView}
        setPageView={setPageView}
      />

      <Box
        sx={{
          p: 3,
          pt: 0,
          overflowY: 'auto',
          display: isCollabFlowActive || pageView === 'list' ? 'none' : 'block',
        }}
        ref={elementRef}
      >
        <InfiniteScroll
          isLoading={isLoading}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          restrictFetching={
            venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan
          }
        >
          <ProfileCheckBoxGroup
            data={data}
            isLoading={isLoading}
            isFetching={isFetching}
            onChange={handleUserSelection}
            values={selected.values}
            handleMarketChange={handleMarketChange}
          />
        </InfiniteScroll>
      </Box>

      <Box sx={{ display: isCollabFlowActive || pageView === 'grid' ? 'none' : 'block' }}>
        <CollabUsersTable
          data={data}
          isLoading={isLoading}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          onRowSelction={handleUserSelection}
          handleMarketChange={handleMarketChange}
          handleSorting={handleSorting}
          restrictFetching={
            venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan
          }
          pageView={pageView}
          selectedUsers={selected.values}
          hasNoSubscriptionPlan={
            !subscriptionPlan && venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
          }
        />
      </Box>

      {isCollabFlowActive && <CollabInvite reducer={[state, dispatch]} onBack={onSuccess} />}

      {venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) &&
        !subscriptionPlan &&
        inScroll && <UpgradePlanOverlay />}
    </Box>
  );
};

export default Collabs;
